let openedDropDownParent = null;

// Get an event's target element and the element specified by the "data-target" attribute
function getTargets(event) {
  var targets = {};
  event = event || window.event;
  targets.evTarget = event.currentTarget || event.srcElement;
  var dataTarget = targets.evTarget.getAttribute('data-target');
  targets.dataTarget = dataTarget ? document.querySelector(dataTarget) : false;
  return targets;
}

function findAncestor(el, cls) {
  let elm = el;
  while (elm && !elm.classList.contains(cls)) {
    elm = elm.parentElement;
  }
  return elm;
}

function clickOutside(event) {
  if (!findAncestor(event.target, 'dropdown-menu')) {
    openedDropDownParent.classList.remove('open');
    openedDropDownParent = null;
    document.body.removeEventListener('click', clickOutside);
  }
}

// Show a dropdown menu
function doDropdown(event = window.event) {
  event.preventDefault();
  event.stopPropagation();
  const evTarget = event.currentTarget || event.srcElement;
  openedDropDownParent = evTarget.parentElement;
  openedDropDownParent.classList.toggle('open');
  document.body.addEventListener('click', clickOutside);
  return false;
}

// Track initialized dropdowns to prevent duplicate event listeners
const initializedDropdowns = new WeakSet();

// Set event listeners for dropdown menus
export function initDropdowns() {
  const dropdownList = document.querySelectorAll('[data-toggle=dropdown]');
  for (let k = 0, dropdown, lenk = dropdownList.length; k < lenk; k++) {
    dropdown = dropdownList[k];

    // Skip if already initialized
    if (initializedDropdowns.has(dropdown)) {
      continue;
    }

    dropdown.setAttribute('tabindex', '0'); // Fix to make onblur work in Chrome
    dropdown.addEventListener('click', doDropdown, true);

    // Mark as initialized
    initializedDropdowns.add(dropdown);
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDropdowns);
} else {
  // DOM is already ready
  initDropdowns();
}

// Use MutationObserver to watch for dynamically added dropdowns
// This is more reliable than setTimeout and catches all dynamically rendered dropdowns
const dropdownObserver = new MutationObserver((mutations) => {
  let shouldReinitialize = false;

  for (const mutation of mutations) {
    // Check if any added nodes contain dropdown triggers
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        // Check if the node itself or any descendants have data-toggle=dropdown
        if (
          node.hasAttribute?.('data-toggle') && node.getAttribute('data-toggle') === 'dropdown' ||
          node.querySelector?.('[data-toggle=dropdown]')
        ) {
          shouldReinitialize = true;
          break;
        }
      }
    }
    if (shouldReinitialize) break;
  }

  if (shouldReinitialize) {
    initDropdowns();
  }
});

// Start observing after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    dropdownObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
} else {
  dropdownObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
}

// transitionend - source: https://stackoverflow.com/questions/5023514/how-do-i-normalize-css3-transition-functions-across-browsers#answer-9090128
function transitionEndEventName() {
  var i,
    el = document.createElement('div'),
    transitions = {
      transition: 'transitionend',
      OTransition: 'otransitionend', // oTransitionEnd in very old Opera
      MozTransition: 'transitionend',
      WebkitTransition: 'webkitTransitionEnd'
    };

  for (i in transitions) {
    if (transitions.hasOwnProperty(i) && el.style[i] !== undefined) {
      return transitions[i];
    }
  }

  return false;
}

const transitionend = transitionEndEventName();

// Get the potential max height of an element
function getMaxHeight(element) {
  // Source: http://n12v.com/css-transition-to-from-auto/
  var prevHeight = element.style.height;
  element.style.height = 'auto';
  var maxHeight = getComputedStyle(element).height;
  element.style.height = prevHeight;
  element.offsetHeight; // force repaint
  return maxHeight;
}

/*
 * Collapse action
 * 1. Get list of all elements that are collapse triggers
 * 2. Add click event listener to these elements
 * 3. When clicked, change target element's class name from "collapse" to "collapsing"
 * 4. When action (collapse) is complete, change target element's class name from
 * "collapsing" to "collapse in"
 * 5. Do the reverse, i.e. "collapse in" -> "collapsing" -> "collapse"
 */

// Change classes once transition is complete
function complete(element) {
  element.classList.remove('collapsing');
  element.classList.add('collapse');
  element.setAttribute('aria-expanded', false);

  // Check whether the element is unhidden
  if (element.style.height !== '0px') {
    element.classList.add('in');
    element.style.height = 'auto';
  }
}

// Show a target element
function show(element, trigger) {
  element.classList.remove('collapse');
  element.classList.add('collapsing');
  trigger.classList.remove('collapsed');
  trigger.setAttribute('aria-expanded', true);

  // Set element's height to its maximum height
  element.style.height = getMaxHeight(element);

  // Call the complete() function after the transition has finished
  if (transitionend) {
    element.addEventListener(
      transitionend,
      () => {
        complete(element);
      },
      false
    );
  } else {
    // For browsers that don't support transitions (e.g. IE9 and lower);
    complete(element);
  }
}

// Hide a target element
function hide(element, trigger) {
  element.classList.remove('collapse');
  element.classList.remove('in');
  element.classList.add('collapsing');
  trigger.classList.add('collapsed');
  trigger.setAttribute('aria-expanded', false);

  // Reset element's height
  element.style.height = getComputedStyle(element).height;
  element.offsetHeight; // force repaint
  element.style.height = '0px';
}

// Start the collapse action on the chosen element
function doCollapse(event) {
  event.preventDefault();
  var targets = getTargets(event);
  var dataTarget = targets.dataTarget;

  // Add the "in" class name when elements are unhidden
  if (dataTarget.classList.contains('in')) {
    hide(dataTarget, targets.evTarget);
  } else {
    show(dataTarget, targets.evTarget);
  }
  return false;
}

// Get all elements that are collapse triggers and add click event listeners
function initCollapse() {
  var collapsibleList = document.querySelectorAll('[data-toggle=collapse]');
  for (var i = 0, leni = collapsibleList.length; i < leni; i++) {
    collapsibleList[i].onclick = doCollapse;
  }
}

// Initialize collapse when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollapse);
} else {
  // DOM is already ready
  initCollapse();
}
